import type { ReactNode } from 'react';
import { SEARCH_WORDS } from '@claims/core';
import { ApiError } from './api.js';
import { PressButton } from './press-button.js';

/** THE SHARED STATES (ported 2026-10-18 from the medrec console's QueryState and SignInScreen, its 10-17 redesign; DESIGN_SYSTEM 12): what is
 *  true, never a blank screen, a raw error or a lone "Loading" line.
 *
 *  - LOADING: a skeleton; the words stay for a screen reader (and for the layout audit, which waits for them to go). A list page's first
 *    read draws skeleton rows at the rows' own height (`rows`; the medrec console's ListSkeleton, its PR #176), a page's other reads a few
 *    bars; both breathe only once a read has taken 300 ms (medrec-look.css `sk-pulse`).
 *  - A LOAD ERROR (nothing on screen yet): "Could not load <what>", the API's own words (the API is the only thing that knows why), "Nothing
 *    changed", and Try again. A read that fails while an earlier answer is on screen keeps that answer; the top bar's clock says it, amber,
 *    with Try again (Shell.tsx). A person never reads "Retry" (the medrec console's redesign follow-ups, its PR #166; test/try-again.test.ts).
 *  - EMPTY and NO MATCH: one line that closes the loop, and the next step beside it where there is one ("Clear the search").
 *  - THE SIGN-IN (main.tsx) and THE ACCOUNT (Shell.tsx): while Microsoft signs a person in, when sign-in is not set up, when the API refuses
 *    the sign-in (no claims console role: "Ask Rod to add you") or no longer accepts it (it ran out), the logo and the words in the sign-in
 *    box. This console signs in by itself (it never shows a button to press first), so the box only ever says what is happening. */
export const STATE_WORDS = Object.freeze({
  couldNot: (what: string): string => `Could not load ${what}`,
  nothingChanged: 'Nothing changed.',
  tryAgain: 'Try again',
  product: 'Sweet Claims',
  line: 'The claims team\u{2019}s console',
  signingIn: 'Signing you in with Microsoft.',
  who: 'Use your Sweet James account. Only people on the claims team can sign in; ask Rod for access.',
  ranOut: 'Your sign-in ran out',
  ranOutLine: 'It lasts 24 hours. Open a new tab to sign in again; nothing you were doing is lost.',
  noAccess: 'You are signed in, but not on the claims team yet',
  noAccessLine: 'Ask Rod to add you. Until then this console shows nothing.',
  clearSearch: 'Clear the search',
  /** a list the top bar's search emptied: the medrec console's words (core SEARCH_WORDS.none, its PR #166), on every list */
  noMatch: (q: string): string => SEARCH_WORDS.none(q),
});

const LOGO = '/brand/sweetclaims-logo-180.png';

/** A page's read, as the page holds it (TanStack Query's own fields). */
export interface PageRead { isLoading: boolean; error: unknown; data?: unknown; refetch: () => unknown }

/** How many skeleton rows a list's first read draws (about one screen of 48 px rows; the medrec console's LIST_SKELETON_ROWS). */
export const LIST_SKELETON_ROWS = 8;

/** Loading or a failed first read; nothing once there is an answer (a later failed read is the clock's to say). `rows`: a list page, whose
 *  skeleton is rows; `skeleton`: a list that draws its own (the Queue's table in its own columns, queue.tsx QueueSkeleton). Try again is a
 *  press (press.ts): "Trying again" with its spinner until the read answers. */
export function QueryState({ q, what, loading, rows = false, skeleton }: { q: PageRead; what: string; loading: string; rows?: boolean; skeleton?: ReactNode }) {
  if (q.data !== undefined) return null;
  if (q.error) {
    const words = q.error instanceof Error ? q.error.message.replace(/\.$/, '') : 'The console could not reach its server';
    return (
      <div className="qs-card" role="alert" data-testid="query-error" data-state="error">
        <p className="qs-h">{STATE_WORDS.couldNot(what)}</p>
        <p className="qs-p">{`${words}. ${STATE_WORDS.nothingChanged}`}</p>
        <div className="qs-acts"><PressButton label={STATE_WORDS.tryAgain} className="qs-pri" onPress={() => q.refetch()} /></div>
      </div>
    );
  }
  if (q.isLoading && skeleton !== undefined) {
    return (
      <div className="qs-cols" aria-busy="true" data-testid="query-loading">
        <span className="qs-sr">{loading}</span>
        {skeleton}
      </div>
    );
  }
  if (q.isLoading && rows) {
    return (
      <div className="qs-rows" aria-busy="true" data-testid="query-loading">
        <span className="qs-sr">{loading}</span>
        {Array.from({ length: LIST_SKELETON_ROWS }, (_, i) => (
          <div key={i} className="qs-skrow" aria-hidden="true" data-testid="list-skeleton-row"><span className="qs-sk qs-w80" /><span className="qs-sk qs-w60" /></div>
        ))}
      </div>
    );
  }
  if (q.isLoading) {
    return (
      <div className="qs-loading" aria-busy="true" data-testid="query-loading">
        <span className="qs-sr">{loading}</span>
        <span className="qs-sk" aria-hidden="true" />
        <span className="qs-sk qs-w80" aria-hidden="true" />
        <span className="qs-sk qs-w60" aria-hidden="true" />
        <span className="qs-sk" aria-hidden="true" />
        <span className="qs-sk qs-w80" aria-hidden="true" />
      </div>
    );
  }
  return null;
}

/** An empty list or no match: the one line, and its next step where there is one. `inline` inside a card that already has a border. */
export function EmptyState({ words, next, inline = false }: { words: string; next?: { label: string; onPress: () => void } | null; inline?: boolean }) {
  return (
    <div className={inline ? 'qs-empty inline' : 'qs-empty'} data-testid="empty-state">
      <span>{words}</span>
      {next ? <button type="button" className="qs-next" onClick={next.onPress}>{next.label}</button> : null}
    </div>
  );
}

/** The sign-in box: the logo, the name, one line, then what is happening (signing in, or why it cannot). */
export function SignInScreen({ children }: { children: ReactNode }) {
  return (
    <main className="signin" data-testid="signin">
      <div className="signin-box">
        <img className="signin-logo" src={LOGO} alt="" width={64} height={64} />
        <h1>{STATE_WORDS.product}</h1>
        <p>{STATE_WORDS.line}</p>
        <div className="signin-state" role="status">{children}</div>
        <small>{STATE_WORDS.who}</small>
      </div>
    </main>
  );
}

/** Who-is-signed-in's own failure, in the sign-in box: the API refused the sign-in (403: no claims console role) or no longer accepts it
 *  (401: it ran out). Any other failure is a page's own load error, not the account's. */
export function accountState(error: unknown): { title: string; line: string } | null {
  if (!(error instanceof ApiError)) return null;
  if (error.status === 403) return { title: STATE_WORDS.noAccess, line: STATE_WORDS.noAccessLine };
  if (error.status === 401) return { title: STATE_WORDS.ranOut, line: STATE_WORDS.ranOutLine };
  return null;
}
export function AccountScreen({ state }: { state: { title: string; line: string } }) {
  return (
    <SignInScreen>
      <p className="qs-h" data-testid="account-state">{state.title}</p>
      <p className="qs-p">{state.line}</p>
    </SignInScreen>
  );
}
